import {observer} from "mobx-react";
import React, {lazy, useEffect, useRef, useState} from "react";
import {fileTypeEnum} from "../../serverClientShared/sharedEnums";
import Utility from "../../Utility";
import {Button, Card, CircularProgress, Dialog, Popover} from "@mui/material";
import 'tui-image-editor/dist/tui-image-editor.css';
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import CropIcon from "@mui/icons-material/Crop";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from "prop-types";
import TheatersIcon from '@mui/icons-material/Theaters';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import ConditionalComponent from "../Shared/ConditionalComponent";
import {itemTypeEnum} from "../../enums";
import {fusionTypes} from "../../serverClientShared/sharedUtils";
import {useStores} from "../../Hooks";
import EllipsisContainer from "../Shared/EllipsisContainer";

// noinspection JSCheckFunctionSignatures
const ImageEditor = lazy(() => import('@toast-ui/react-image-editor'));

const ItemsPreview = observer(({items, refreshMediaLibrary, selectedFiles}) => {
    const {modalStore, generalStore, projectStore, procedureStore} = useStores();

    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [singleFilePopulated, setSingleFilePopulated] = useState({});
    const [editImageDialogOpen, setEditImageDialogOpen] = useState(false);
    const [showFullScreenHint, setShowFullScreenHint] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const editorRef = useRef(null);

    const isSingleFile = items.length === 1 && items[0].type === itemTypeEnum.FILE;
    const firstItem = items ? items[0] : null;
    useEffect(() => {
        if (isSingleFile && singleFilePopulated._id !== firstItem._id) {
            let projectId;
            if (projectStore?.currentProject?.ProjectIdentifier) {
                projectId = projectStore.currentProject?.ProjectIdentifier;
            } else if (procedureStore?.currentProcedure?.ProjectRef) {
                projectId = procedureStore.currentProcedure.ProjectRef;
            } else {
                console.error('missing project ref');
                return;
            }
            projectStore.getFileDetails(projectId, firstItem._id).then(setSingleFilePopulated);
            if(firstItem && singleFilePopulated.FileId)
                firstItem.FileId = singleFilePopulated.FileId;
        }

    }, [firstItem, isSingleFile, singleFilePopulated, procedureStore.currentProcedure.ProjectRef, projectStore.currentProject?.ProjectIdentifier, projectStore]);

    useEffect(()=>{
        if(firstItem && singleFilePopulated.FileId && selectedFiles[0].FileId !== singleFilePopulated.FileId) {
            selectedFiles[0].FileId = singleFilePopulated.FileId;
        }
        // eslint-disable-next-line
    },[singleFilePopulated]);

    let itemFileType;

    let itemTitle;
    if (!items.length) {
        itemTitle = '';
    } else if (items.length === 1) {
        const singleItem = items[0];
        if (singleItem.type === itemTypeEnum.MEDIA_FOLDER) {
            itemTitle = `${singleItem.FolderName}`;
        } else {
            itemTitle = `${singleItem.FileName}.${singleItem.FileExtension}`;
            itemFileType = singleItem.FileType;
        }
    } else {
        itemTitle = `${items.length} ${generalStore.translate('itemsSelected_afterNumber')}`;
    }

    function renderFilePreview() {
        if (!isSingleFile || !singleFilePopulated._id) {
            return <></>;
        }
        return Utility.renderFilePreview(singleFilePopulated, generalStore.translate);
    }


    function renderFileSize(size) {
        if (!size) {
            return null;
        }
        if (size < 1024) {
            return size + ' B';
        }
        size = Math.round(size / 1024);
        if (size < 1024) {
            return size + ' KB';
        }
        size = Math.round(size / 1024);
        if (size < 1024) {
            return size + ' MB';
        }
        size = Math.round(size / 1024);
        return size + ' GB';
    }

    function renderThumbnail() {
        if (!items.length) {
            return null;
        }

        if (!isSingleFile) {
            return (
                <div className='multiple-icons'>
                    <TheatersIcon/>
                    <TheatersIcon/>
                </div>
            )
        }

        const file = items[0];
        const thumb = Utility.renderFileThumbnail(file);
        return (
            <div
                className='file-thumbnail-container'
                onMouseEnter={() => setShowFullScreenHint(true)}
                onMouseLeave={() => setShowFullScreenHint(false)}
            >
                <ConditionalComponent if={showFullScreenHint}>
                    <div className='fullscreen-button-container'>
                        <FullscreenIcon/>
                    </div>
                </ConditionalComponent>
                {thumb}
            </div>
        );
    }

    function openUsedProcedures(e) {
        setAnchorEl(e.currentTarget);
    }

    function closeUsedProcedures() {

        setAnchorEl(null);
    }

    function renderFileProps() {
        if (!isSingleFile) {
            return;
        }
        if (!singleFilePopulated._id) {
            return <div>
                <CircularProgress/>
            </div>;
        }
        const file = singleFilePopulated;
        const procedures = file.procedures || [];
        const len = procedures.length;
        const addedBy = file.AddedBy ? `${file.AddedBy.Name} ${file.AddedBy.LastName}` : '';

        return <div className='file-properties'>
            <ConditionalComponent if={!!file.FileExtension}>
                <div className='file-property'>
                    <div>{generalStore.translate('type')}:</div>
                    <div>{file.FileExtension}</div>
                </div>
            </ConditionalComponent>

            <ConditionalComponent if={!!file.Size}>
                <div className='file-property'>
                    <div>{generalStore.translate('size')}:</div>
                    <div className="number-spread">{renderFileSize(file.Size)}</div>
                </div>
            </ConditionalComponent>
            <ConditionalComponent if={!!file.DateCreated}>
                <div className='file-property'>
                    <div>{generalStore.translate('dateAdded')}:</div>
                    <div>{Utility.renderFullDate(file.DateCreated, generalStore.translate('dateLocale'))}</div>
                </div>
            </ConditionalComponent>

            <ConditionalComponent if={!!file.AddedBy}>
                <div className='file-property'>
                    <div>{generalStore.translate('addedBy')}:</div>

                    <EllipsisContainer value={addedBy}>
                        <div className='added-by'>
                            {addedBy}
                        </div>
                    </EllipsisContainer>
                </div>
            </ConditionalComponent>

            <div className='file-property'>
                <div>{generalStore.translate('usedIn')}:</div>
                <div>
                    <span>{`${len} ${generalStore.translate(len !== 1 ? "flows" : "flow")}`}</span>
                    <span
                        className="used-procedures-arrow"
                        onClick={(e) => len > 0 && openUsedProcedures(e)}
                    >
                        {!!anchorEl
                            ? <ExpandLessIcon/>
                            : <ExpandMoreIcon/>
                        }
                    </span>
                </div>
                <Popover
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    onClose={closeUsedProcedures}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: generalStore.langDir === 'rtl' ? "left" : "right",
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: generalStore.langDir === 'rtl' ? "left" : "right",
                    }}
                >
                    <div className='file-used-procedures-list special-scroll'>
                        {procedures.map((p, i) =>
                            <div key={i}>{`- ${p.ProcedureName}`}</div>
                        )}
                    </div>
                </Popover>
            </div>

        </div>;
    }

    async function saveEdits() {

        if (!isSingleFile) {
            return;
        }
        const file = items[0];
        delete file.type;
        try {
            generalStore.isLoaderOpen = true;
            editorRef.current.getInstance().deactivateAll(); // remove markings
            const fabricCanvas = editorRef.current.getInstance()._graphics.getCanvas();
            const blob = await new Promise(resolve => fabricCanvas.getElement().toBlob(blob => resolve(blob)));
            await generalStore.overrideFile(blob, file);
            setEditImageDialogOpen(false);
            await refreshMediaLibrary();
        } catch (e) {
            modalStore.showErrorMessage(generalStore.translate('cannotSaveEdits'));
        }
        generalStore.isLoaderOpen = false;
    }

    function renderDialogs() {
        if (!isSingleFile) {
            return;
        }
        const file = items[0];
        return <>
            <Dialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                maxWidth='md'
                className='file-preview-dialog'
            >
                {renderFilePreview()}
            </Dialog>

            <Dialog
                id='image-editor-dialog'
                open={editImageDialogOpen}
                onClose={() => setEditImageDialogOpen(false)}
                maxWidth='lg'
                TransitionProps={{
                    onEnter: () => Utility.uglyDisableFocusStealing('image-editor-dialog')
                }}
            >
                <Button className='save-edited-image' onClick={saveEdits} color='primary' variant='contained'>
                    {generalStore.translate('save')}
                </Button>
                <ImageEditor
                    ref={editorRef}
                    includeUI={{
                        loadImage: {
                            path: file.FileId,
                            name: file.FileName
                        },
                        menu: ['crop', 'flip', 'rotate', 'draw', 'shape', 'icon', 'text', 'mask', 'filter'],
                        initMenu: 'filter',
                        uiSize: {
                            width: '1000px',
                            height: '700px'
                        },
                        menuBarPosition: 'bottom'
                    }}
                    cssMaxHeight={500}
                    cssMaxWidth={700}
                    selectionStyle={{
                        cornerSize: 20,
                        rotatingPointOffset: 70
                    }}
                    usageStatistics={false}
                />
            </Dialog>
        </>;
    }

    async function deleteItems() {
        if (!items.length) {
            return;
        }
        modalStore.modalData.afterDelete = refreshMediaLibrary;
        modalStore.modalData.itemsToDelete = items;
        modalStore.modalData.headerClassName = 'create-media-folder-header';
        modalStore.isDeleteMediaItemsModalOpen = true;
    }

    function isDeleteDisabled() {
        return items.length === 1 &&
            items[0].type === itemTypeEnum.MEDIA_FOLDER &&
            items[0]._id === projectStore.currentProject?.UploadFolderId;
    }

    const handleReplaceMedia = ()=> {
        generalStore.oldMediaFileToBeReplaced = singleFilePopulated;
        generalStore.oldMediaFilesToBeReplaced.push(singleFilePopulated);
        if(singleFilePopulated.procedures.length>0) {
            modalStore.modalData.headerClassName = 'create-media-folder-header';
            modalStore.isReplaceMediaModalOpen = true;
        }
        else{
            modalStore.isUploadModalOpen = true;
        }
    }

    return (
        <div className='file-details'>
            <Tooltip
                title='click to preview'
                arrow
                onClick={() => {
                    if (items.length === 1 && items[0].type === itemTypeEnum.FILE &&
                        ![
                            fileTypeEnum.THREE_D,
                            ...fusionTypes
                        ].includes(items[0].FileType)
                    ) {
                        setPreviewDialogOpen(true);
                    }
                }}
                disableHoverListener={items.length !== 1 || items[0].type !== itemTypeEnum.FILE}
            >
                <Card className='file-preview'>
                    {renderThumbnail()}
                </Card>
            </Tooltip>
            <ConditionalComponent if={!!itemTitle}>
                <EllipsisContainer value={itemTitle}>
                    <div className='filename-container'>
                        {itemTitle}
                    </div>
                </EllipsisContainer>
            </ConditionalComponent>

            <Divider/>

            <ConditionalComponent if={itemFileType === fileTypeEnum.IMAGE}>
                <div className='edit-button-container'>
                    <Button onClick={() => setEditImageDialogOpen(true)}>
                        <CropIcon/>
                        {generalStore.translate('cropAndEdit')}
                    </Button>
                </div>
            </ConditionalComponent>


            {renderFileProps()}

            <ConditionalComponent if={!!items.length}>
                <ConditionalComponent if={Object.keys(singleFilePopulated).length !== 0 && items.length === 1 && (items[0].FileType === fileTypeEnum.IMAGE || items[0].FileType === fileTypeEnum.VIDEO || items[0].FileType === fileTypeEnum.PDF)}>
                <div className='delete-button-container'>
                    <Button
                        className='cancel-primary-button media-manager'
                        variant='contained'
                        onClick={handleReplaceMedia}
                    >
                        {generalStore.translate('replace')}
                    </Button>
                </div>
                </ConditionalComponent>
                <div className='delete-button-container'>
                    <Button
                        className='delete-button delete'
                        disabled={isDeleteDisabled()}
                        onClick={deleteItems}
                    >
                        {(items.length > 1 ? generalStore.translate('deleteItems') : generalStore.translate('deleteItem'))}
                    </Button>
                </div>
            </ConditionalComponent>
            {renderDialogs()}
        </div>
    )

});
ItemsPreview.propTypes = {
    items: PropTypes.array.isRequired,
    refreshMediaLibrary: PropTypes.func
};

export default ItemsPreview;
